/**
 * Collection Product Renderer
 * 
 * Handles rendering products from JSON data into a collection grid.
 * Implements product grouping logic matching the Liquid template.
 * 
 * Usage:
 *   const renderer = new CollectionProductRenderer({
 *     container: document.getElementById('product-grid'),
 *     collectionHandle: 'collection-handle',
 *     jsonLoader: collectionJsonLoader,
 *     sectionSettings: { ... }
 *   });
 *   await renderer.render();
 */

class CollectionProductRenderer {
  constructor(options) {
    this.container = options.container;
    this.collectionHandle = options.collectionHandle;
    this.jsonLoader = options.jsonLoader;
    this.sectionSettings = options.sectionSettings || {};
    this.sectionId = options.sectionId || '';
    this.routes = options.routes || window.routes || {};
  }

  /**
   * Parse current URL search params for sort_by and filter.* (Shopify facet format).
   */
  getSearchParams() {
    const search = typeof window !== 'undefined' && window.location && window.location.search
      ? window.location.search.slice(1)
      : '';
    const params = new URLSearchParams(search);
    const sort_by = params.get('sort_by') || '';
    const filters = {};
    params.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        const filterKey = key.slice(7);
        if (!filters[filterKey]) filters[filterKey] = [];
        filters[filterKey].push(value);
      }
    });
    return { sort_by, filters: filters };
  }

  /**
   * Parse price filter value to cents. URL params from Shopify theme use money_without_currency (e.g. 1267 for $1267).
   * Product/variant price in JSON is in cents.
   */
  parsePriceToCents(value) {
    if (value == null || value === '') return null;
    const num = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return null;
    if (num >= 100000) return num;
    return Math.round(num * 100);
  }

  /**
   * Filter products by Shopify filter params (e.g. filter.p.vendor, filter.v.price.gte, filter.v.availability, filter.p.m.*).
   */
  applyFilters(products, filterParams) {
    if (!products || !filterParams || Object.keys(filterParams).length === 0) {
      return products;
    }
    const filterValueNorm = (v) => (v == null ? '' : String(v).trim());
    const toValues = (raw) => {
      const arr = Array.isArray(raw) ? raw : [raw];
      return arr.map(filterValueNorm);
    };
    return products.filter((product) => {
      for (const paramKey of Object.keys(filterParams)) {
        const allVals = toValues(filterParams[paramKey]);

        if (paramKey === 'v.availability') {
          const filterValue = allVals[0] || '';
          const hasAvailableVariant = product.variants && product.variants.some((v) => v.available !== false);
          if (filterValue === '1') {
            if (!hasAvailableVariant) return false;
          } else if (filterValue === '0') {
            if (hasAvailableVariant) return false;
          }
          continue;
        }

        const values = allVals.filter((v) => v !== '');
        if (values.length === 0) continue;

        if (paramKey === 'p.vendor') {
          const productVendor = filterValueNorm(product.vendor);
          if (values.some((fv) => productVendor === fv)) continue;
          return false;
        }
        if (paramKey === 'p.product_type') {
          const productType = filterValueNorm(product.product_type || '');
          if (values.some((fv) => productType === fv)) continue;
          return false;
        }
        if (paramKey === 'p.tag') {
          const tags = Array.isArray(product.tags) ? product.tags : [];
          const hasTag = values.some((fv) => tags.some((t) => filterValueNorm(t) === fv));
          if (hasTag) continue;
          return false;
        }
        if (paramKey === 'v.price.gte') {
          const minCents = this.parsePriceToCents(values[0]);
          if (minCents != null) {
            const productPrice = Number(product.price) || 0;
            if (productPrice < minCents) return false;
          }
          continue;
        }
        if (paramKey === 'v.price.lte') {
          const maxCents = this.parsePriceToCents(values[0]);
          if (maxCents != null) {
            const productPrice = Number(product.price) || 0;
            if (productPrice > maxCents) return false;
          }
          continue;
        }
        if (paramKey.startsWith('p.m.')) {
          const metafieldPath = paramKey.slice(4);
          const raw = product.metafields;
          const parts = metafieldPath.split('.');
          let jsonVal;
          if (raw) {
            if (parts.length === 2) {
              jsonVal = raw[parts[0]]?.[parts[1]]?.value;
            } else {
              jsonVal = raw[metafieldPath]?.value;
            }
          }
          const matchesOne = (filterValue) => {
            const productValue = this.getMetafieldValue(product, metafieldPath);
            const productStr = filterValueNorm(productValue);
            if (productStr === filterValue) return true;
            if (typeof jsonVal === 'string' && jsonVal.startsWith('[')) {
              try {
                const arr = JSON.parse(jsonVal);
                if (Array.isArray(arr) && arr.some((v) => filterValueNorm(v) === filterValue)) return true;
              } catch (e) {}
            }
            return false;
          };
          if (values.some(matchesOne)) continue;
          return false;
        }
        if (paramKey.startsWith('v.option.')) {
          const variant = product.variants && product.variants[0];
          const variantTitle = variant ? filterValueNorm(variant.title) : '';
          if (values.some((fv) => variantTitle === fv)) continue;
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Sort products by Shopify sort_by value (title, price, created; best-selling falls back to default).
   */
  applySort(products, sortBy) {
    if (!products || products.length === 0 || !sortBy) return products;
    const list = [...products];
    const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
    switch (sortBy) {
      case 'title-ascending':
        list.sort((a, b) => cmp((a.title || '').toLowerCase(), (b.title || '').toLowerCase()));
        break;
      case 'title-descending':
        list.sort((a, b) => cmp((b.title || '').toLowerCase(), (a.title || '').toLowerCase()));
        break;
      case 'price-ascending':
        list.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
        break;
      case 'price-descending':
        list.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
        break;
      case 'created-ascending':
        list.sort((a, b) => {
          const ta = (a.created_at && new Date(a.created_at).getTime()) || 0;
          const tb = (b.created_at && new Date(b.created_at).getTime()) || 0;
          return ta - tb;
        });
        break;
      case 'created-descending':
        list.sort((a, b) => {
          const ta = (a.created_at && new Date(a.created_at).getTime()) || 0;
          const tb = (b.created_at && new Date(b.created_at).getTime()) || 0;
          return tb - ta;
        });
        break;
      case 'best-selling':
        break;
      default:
        break;
    }
    return list;
  }

  /**
   * Render products from JSON data (applies URL filters and sort, then renders).
   */
  async render() {
    if (!this.jsonLoader || !this.jsonLoader.isLoaded()) {
      throw new Error('JSON loader not initialized or data not loaded');
    }

    let products = this.jsonLoader.getProductsByHandle(this.collectionHandle);
    if (!products || products.length === 0) {
      console.warn(`No products found for collection: ${this.collectionHandle}`);
      return;
    }

    const { sort_by, filters } = this.getSearchParams();
    products = this.applyFilters(products, filters);
    products = this.applySort(products, sort_by);

    if (this.sectionSettings.enable_product_grouping && this.sectionSettings.grouping_metafield) {
      return this.renderGroupedProducts(products);
    }
    return this.renderRegularProducts(products);
  }

  /**
   * Compare two grouping/variant values for sort order (BTU, Ton, %, etc.).
   * Uses numeric comparison when values contain numbers (e.g. "1.5 Ton", "2 Ton", "60,000 BTU", "80%").
   */
  compareGroupingValues(a, b) {
    const aStr = String(a || '').trim();
    const bStr = String(b || '').trim();
    const toNum = (s) => {
      if (!s || typeof s !== 'string') return NaN;
      const cleaned = s.replace(/,/g, '').trim();
      return parseFloat(cleaned);
    };

    // Leading number: digits with optional commas/decimals (e.g. "60,000 BTU", "1.5 Ton", "2 Ton")
    const aNumMatch = aStr.match(/^([\d,.]+)/);
    const bNumMatch = bStr.match(/^([\d,.]+)/);
    if (aNumMatch && bNumMatch) {
      const aNum = toNum(aNumMatch[1]);
      const bNum = toNum(bNumMatch[1]);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    }

    // Percentage (e.g. "80%", "95%")
    const aPct = aStr.match(/^(\d+(?:\.\d+)?)\s*%/);
    const bPct = bStr.match(/^(\d+(?:\.\d+)?)\s*%/);
    if (aPct && bPct) {
      const an = parseFloat(aPct[1]);
      const bn = parseFloat(bPct[1]);
      return an - bn;
    }

    // Plain numbers (e.g. "1", "2", "3")
    const an = parseFloat(aStr);
    const bn = parseFloat(bStr);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;

    return aStr.localeCompare(bStr);
  }

  /**
   * Render products with grouping logic, sorted by grouping factor (e.g. BTU, Ton).
   */
  renderGroupedProducts(products) {
    const groupingMetafield = this.sectionSettings.grouping_metafield;
    const diffMetafield = this.sectionSettings.differentiative_metafield;
    const differentiateByVendor = this.sectionSettings.differentiate_by_vendor === true;
    
    const getDiffValue = (product) => {
      if (differentiateByVendor) return (product.vendor || '').trim();
      return diffMetafield ? this.getMetafieldValue(product, diffMetafield) : '';
    };
    
    // Extract tonnage from tags ONLY when groupingValue is a range (e.g., "3.5 - 5 Ton")
    // This allows evaporator coils to be separated into "3.5 Ton", "4 Ton", "4-5 Ton"
    // but keeps "1.5 Ton" products grouped together
    const getTonnageTag = (product, groupingValue) => {
      // Only separate by tonnage if the grouping value contains a dash (indicating a range)
      if (!product.tags || !groupingValue || !groupingValue.includes('-')) return '';
      const tonnageTag = product.tags.find(tag => /\d+(\.\d+)?\s*(-\s*\d+)?\s*Ton/i.test(tag));
      return tonnageTag || '';
    };
    
    const seen = new Set();
    const groups = [];
    const ungrouped = [];

    products.forEach((product) => {
      const groupingValue = this.getMetafieldValue(product, groupingMetafield);
      if (!groupingValue) {
        ungrouped.push({ product, isUngrouped: true });
        return;
      }
      const diffValue = getDiffValue(product);
      const tonnageTag = getTonnageTag(product, groupingValue);
      const groupKey = this.createGroupKey(groupingValue, diffValue, tonnageTag);
      if (seen.has(groupKey)) return;
      seen.add(groupKey);

      const groupProducts = products.filter(p => {
        const pGroupingValue = this.getMetafieldValue(p, groupingMetafield);
        const pDiffValue = getDiffValue(p);
        const pTonnageTag = getTonnageTag(p, pGroupingValue);
        // Normalize values for comparison to handle whitespace and case differences
        const normalizeValue = (val) => val ? String(val).trim().toLowerCase() : '';
        return normalizeValue(pGroupingValue) === normalizeValue(groupingValue) && 
               normalizeValue(pDiffValue) === normalizeValue(diffValue) &&
               normalizeValue(pTonnageTag) === normalizeValue(tonnageTag);
      });
      groups.push({ groupKey, groupingValue, diffValue, groupProducts, firstProduct: product });
    });

    groups.sort((ga, gb) => {
      const cmp = this.compareGroupingValues(ga.groupingValue, gb.groupingValue);
      if (cmp !== 0) return cmp;
      return this.compareGroupingValues(ga.diffValue, gb.diffValue);
    });

    const gridItems = [];
    groups.forEach((g, idx) => {
      try {
        const cardHtml = this.buildProductCardHtml(g.firstProduct, gridItems.length, true, g.groupProducts.map(p => p.id).join(','), g.groupProducts);
        gridItems.push(`<li class="grid__item">${cardHtml}</li>`);
      } catch (error) {
        console.error(`[GroupedProducts] Error creating card ${idx + 1}:`, error, '| Group:', g.groupingValue);
      }
    });
    ungrouped.forEach(({ product }) => {
      const cardHtml = this.buildProductCardHtml(product, gridItems.length, false);
      gridItems.push(`<li class="grid__item">${cardHtml}</li>`);
    });

    this.container.innerHTML = gridItems.join('');
    this.initializeCardScripts();
  }

  /**
   * Render products without grouping
   */
  renderRegularProducts(products) {
    const gridItems = products.map((product, index) => {
      const cardHtml = this.buildProductCardHtml(product, index, false);
      return `<li class="grid__item">${cardHtml}</li>`;
    });

    this.container.innerHTML = gridItems.join('');
    this.initializeCardScripts();
  }

  /**
   * Get metafield value from product
   */
  getMetafieldValue(product, metafieldPath) {
    if (!metafieldPath) return '';

    // Special handling for tag-based values
    if (metafieldPath.startsWith('tags.')) {
      const tagType = metafieldPath.split('.')[1];
      if (tagType === 'tonnage' && product.tags) {
        // Extract tonnage from tags (e.g., "3.5 Ton", "4 Ton", "4-5 Ton")
        const tonnageTag = product.tags.find(tag => /\d+(\.\d+)?\s*(-\s*\d+)?\s*Ton/i.test(tag));
        return tonnageTag || '';
      }
      return '';
    }

    if (!product.metafields) return '';

    const parts = metafieldPath.split('.');
    let value;

    if (parts.length === 2) {
      // Namespace.key format (e.g., "custom.heating_capacity_btus_")
      const [namespace, key] = parts;
      value = product.metafields[namespace]?.[key]?.value;
    } else {
      // Direct key
      value = product.metafields[metafieldPath]?.value;
    }

    // Handle array values (JSON strings)
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed[0] : value;
      } catch (e) {
        return value;
      }
    }

    return value || '';
  }

  /**
   * Create composite group key
   */
  createGroupKey(groupingValue, diffValue, tonnageTag = '') {
    const normalizedGrouping = String(groupingValue).replace(/\s+/g, '_').toLowerCase();
    let key = normalizedGrouping;
    
    if (diffValue) {
      const normalizedDiff = String(diffValue).replace(/\s+/g, '_').toLowerCase();
      key = `${key}__${normalizedDiff}`;
    }
    
    if (tonnageTag) {
      const normalizedTonnage = String(tonnageTag).replace(/\s+/g, '_').toLowerCase();
      key = `${key}__${normalizedTonnage}`;
    }
    
    return key;
  }


  /**
   * Build product card HTML matching new-card-product.liquid structure.
   * Structure: card > card__inner (image + first card__content) + card__content (main info).
   */
  buildProductCardHtml(product, index, isGrouped, groupProductIds = '', groupProducts = []) {
    const image = product.featured_media || product.featured_image;
    const imageUrl = image?.url || '';
    const imageAlt = image?.alt || product.title;
    const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    const sku = variant ? variant.sku : '';

    // Build image srcset
    const imageWidths = [165, 360, 533, 720, 940, 1066];
    const srcsetParts = imageWidths.map(width => {
      if (image && (image.width || 0) >= width) {
        const url = image.image_url ? image.image_url(width) : imageUrl;
        return `${url} ${width}w`;
      }
      return '';
    }).filter(Boolean);
    if (imageUrl) {
      srcsetParts.push(`${imageUrl} ${(image && image.width) || 1066}w`);
    }
    const srcset = srcsetParts.join(', ') || imageUrl;

    // Price formatting
    const priceFormatted = this.formatPrice(product.price);
    const compareAtPriceFormatted = product.compare_at_price ? this.formatPrice(product.compare_at_price) : null;
    const priceHtml = compareAtPriceFormatted
      ? `<s class="price__compare">${compareAtPriceFormatted}</s><span class="price__current">${priceFormatted}</span>`
      : `<span class="price__current">${priceFormatted}</span>`;

    const cardStyle = this.sectionSettings.card_style || 'standard';
    const ratioPercent = image ? ((1 / (image.aspect_ratio || 1)) * 100) : 100;
    const wrapperTag = isGrouped ? 'grouped-product-card' : 'div';
    const wrapperClass = 'card-wrapper product-card-wrapper card-product-grouped';
    const cardClass = `card card--${cardStyle}${image ? ' card--media' : ' card--text'} color-${this.sectionSettings.color_scheme || 'scheme-1'} gradient`;

    let cardHtml = `<${wrapperTag} class="${wrapperClass}">`;
    cardHtml += `<div class="${cardClass}" style="--ratio-percent: ${ratioPercent}%;">`;
    cardHtml += `<div class="card__inner ratio" style="--ratio-percent: ${ratioPercent}%;">`;

    // Image — HVAC-072-DI: wrap in product link (all collections, mobile + desktop)
    if (image) {
      cardHtml += `
        <div class="card__media">
          <a href="${product.url}" class="card__media-link" data-product-link aria-label="${this.escapeHtml(product.title)}">
            <div class="media media--transparent media--hover-effect">
              <img data-product-image srcset="${this.escapeHtml(srcset)}" src="${this.escapeHtml(imageUrl)}"
                sizes="(min-width: 1200px) calc((100vw - 130px) / 4), (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                alt="${this.escapeHtml(imageAlt)}" class="motion-reduce" loading="${index > 2 ? 'lazy' : 'eager'}" decoding="async"
                width="${image.width || 533}" height="${image.height || 533}">
            </div>
          </a>
        </div>`;
    }

    // First card__content (inside card__inner): title + badge only
    cardHtml += `
      <div class="card__content">
        <div class="card__information">
          <h3 class="card__heading" ${!image ? `id="title-${this.sectionId}-${product.id}"` : ''}>
            <a href="${product.url}" class="full-unstyled-link" id="StandardCardNoMediaLink-${this.sectionId}-${product.id}" aria-labelledby="StandardCardNoMediaLink-${this.sectionId}-${product.id} NoMediaStandardBadge-${this.sectionId}-${product.id}">${this.escapeHtml(product.title)}</a>
          </h3>
        </div>
        <div class="card__badge bottom-left">
          ${!product.available ? `<span id="NoMediaStandardBadge-${this.sectionId}-${product.id}" class="badge badge--bottom-left">Sold out</span>` : ''}
          ${product.available && product.compare_at_price && product.compare_at_price > product.price ? `<span id="NoMediaStandardBadge-${this.sectionId}-${product.id}" class="badge badge--bottom-left">Sale</span>` : ''}
        </div>
      </div>
    </div>`;

    // Second card__content (sibling of card__inner): main product info
    // Order must match Liquid: card__information > h3 (title first), then card-information (vendor, model, variant selectors, price)
    cardHtml += `
      <div class="card__content">
        <div class="card__information">
          <h3 class="card__heading h5" id="title-${this.sectionId}-${product.id}">
            <a href="${product.url}" id="CardLink-${this.sectionId}-${product.id}" class="full-unstyled-link" aria-labelledby="CardLink-${this.sectionId}-${product.id} Badge-${this.sectionId}-${product.id}" data-product-link>
              <span data-product-title>${this.escapeHtml(product.title)}</span>
            </a>
          </h3>
          <div class="card-information dynamic-product-info">`;

    if (this.sectionSettings.show_vendor && product.vendor) {
      cardHtml += `<span class="visually-hidden">Vendor</span><div class="caption-with-letter-spacing light" data-product-vendor>${this.escapeHtml(product.vendor)}</div>`;
    }
    cardHtml += `<span class="caption-large light">Model: <span data-product-model>${this.escapeHtml(sku)}</span></span>`;

    // Variant selectors (grouped only)
    if (isGrouped && groupProducts.length > 0) {
      cardHtml += this.buildVariantSelectorsHtml(product, groupProducts);
    }

    // Price (still inside card-information)
    cardHtml += `
            <div data-product-price>
              <div class="price">
                <div class="price__container">
                  <div class="price__regular">${priceHtml}</div>
                </div>
              </div>
            </div>`;

    // Add to Cart button (quick-add) – matches original card structure for card-product-grouped.js
    const quickAdd = this.sectionSettings.quick_add || 'none';
    if (quickAdd !== 'none') {
      cardHtml += this.buildQuickAddHtml(product, variant);
    }

    cardHtml += `
          </div>
        </div>
      </div>
    </div>`;

    // Grouped product data JSON (for card-product-grouped.js)
    if (isGrouped && groupProducts.length > 0) {
      const groupData = this.buildGroupProductData(groupProducts);
      cardHtml += `<script type="application/json">${JSON.stringify(groupData)}</script>`;
    }

    cardHtml += `</${wrapperTag}>`;
    return cardHtml;
  }

  /**
   * Build Add to Cart (quick-add) form HTML to match original card.
   * Uses .product-variant-id and .quick-add__submit so card-product-grouped.js can update them.
   */
  buildQuickAddHtml(product, variant) {
    const productFormId = `quick-add-${this.sectionId}-${product.id}`;
    const variantId = variant ? variant.id : '';
    const available = product.available !== false;
    const strings = typeof window !== 'undefined' && window.groupedProductCardStrings
      ? window.groupedProductCardStrings
      : { addToCart: 'Add to cart', soldOut: 'Sold out' };
    const buttonText = available ? strings.addToCart : strings.soldOut;
    const cartAddUrl = (this.routes && this.routes.cart_add_url) ? this.routes.cart_add_url : '/cart/add';
    const loadingSpinner = '<div class="loading__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';
    return `
            <div class="quick-add no-js-hidden">
              <product-form data-section-id="${this.sectionId}" data-product-id="${product.id}">
                <form id="${productFormId}" class="form" novalidate="novalidate" data-type="add-to-cart-form" action="${cartAddUrl}" method="post">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" value="${variantId}" class="product-variant-id"${!available ? ' disabled' : ''}>
                  <input type="hidden" name="product-id" value="${product.id}">
                  <input type="hidden" name="section-id" value="${this.sectionId}">
                  <button type="submit" name="add" id="${productFormId}-submit" class="quick-add__submit button button--full-width button--secondary" aria-labelledby="${productFormId}-submit title-${this.sectionId}-${product.id}" aria-live="polite" data-sold-out-message="true"${!available ? ' disabled' : ''}>
                    <span>${this.escapeHtml(buttonText)}</span>
                    <span class="sold-out-message hidden">${this.escapeHtml(strings.soldOut)}</span>
                    ${loadingSpinner}
                  </button>
                </form>
              </product-form>
            </div>`;
  }

  /**
   * Build variant selector HTML (radio inputs) for grouped products.
   * Sets checked on the option that matches the card product for each selector so all three show a selected value.
   */
  buildVariantSelectorsHtml(cardProduct, groupProducts) {
    let html = '';
    const cardProductValueByMetafield = {};
    const labels = [
      { key: 'variant_metafield', label: this.sectionSettings.variant_metafield_label || 'Variant 1' },
      { key: 'variant_metafield_2', label: this.sectionSettings.variant_metafield_2_label || 'Variant 2' },
      { key: 'variant_metafield_3', label: this.sectionSettings.variant_metafield_3_label || 'Variant 3' }
    ];
    
    labels.forEach((cfg, idx) => {
      const metafieldKey = this.sectionSettings[cfg.key];
      if (!metafieldKey) return;
      const num = idx + 1;
      try {
        const cardValue = this.getMetafieldValue(cardProduct, metafieldKey);
        cardProductValueByMetafield[num] = cardValue ? String(cardValue).trim() : '';
      } catch (error) {
        console.error('[VariantSelectors] Error getting card value for', metafieldKey, error);
        cardProductValueByMetafield[num] = '';
      }
    });
    
    labels.forEach((cfg, idx) => {
      const metafieldKey = this.sectionSettings[cfg.key];
      if (!metafieldKey) return;
      const num = idx + 1;
      const seen = new Set();
      const options = [];
      
      try {
        groupProducts.forEach(p => {
          const val = this.getMetafieldValue(p, metafieldKey);
          const valNorm = val ? String(val).trim() : '';
          if (valNorm && !seen.has(valNorm)) {
            seen.add(valNorm);
            options.push({ productId: p.id, value: valNorm, available: p.available });
          }
        });
        
        if (options.length === 0) return;
        
        // Sort tonnage values numerically (e.g., "3.5 Ton", "4 Ton", "4-5 Ton")
        if (metafieldKey === 'tags.tonnage') {
          options.sort((a, b) => {
            const extractNum = (str) => parseFloat(str.match(/(\d+\.?\d*)/)?.[1] || '0');
            return extractNum(a.value) - extractNum(b.value);
          });
        }
        
        const cardValueForSelector = cardProductValueByMetafield[num];
        html += `<div class="product-variant-selector"><span class="variant-selector-label">Select ${this.escapeHtml(cfg.label)}:</span><div class="variant-options">`;
        let checkedSet = false;
        options.forEach((opt, i) => {
          const optId = `variant-${num}-${this.sectionSettings.section_id || 'default'}-${opt.productId}`;
          const valueMatches = cardValueForSelector && opt.value === cardValueForSelector;
          const productMatches = opt.productId === cardProduct.id;
          const isChecked = !checkedSet && (productMatches || valueMatches || (i === 0));
          if (isChecked) checkedSet = true;
          html += `<div class="variant-option${!opt.available ? ' unavailable' : ''}">
            <input type="radio" name="variant-selector-${num}-${cardProduct.id}" value="${opt.productId}" id="${optId}" ${isChecked ? 'checked' : ''}>
            <label for="${optId}" class="variant-swatch">${this.escapeHtml(opt.value)}</label>
          </div>`;
        });
        html += '</div></div>';
      } catch (error) {
        console.error('[VariantSelectors] Error building selector', num, error);
      }
    });
    
    return html;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Build group product data JSON for card-product-grouped.js (price_formatted, etc.)
   */
  buildGroupProductData(groupProducts) {
    return groupProducts.map(product => {
      const image = product.featured_media || product.featured_image;
      const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;

      const raw1 = this.sectionSettings.variant_metafield ? this.getMetafieldValue(product, this.sectionSettings.variant_metafield) : '';
      const raw2 = this.sectionSettings.variant_metafield_2 ? this.getMetafieldValue(product, this.sectionSettings.variant_metafield_2) : '';
      const raw3 = this.sectionSettings.variant_metafield_3 ? this.getMetafieldValue(product, this.sectionSettings.variant_metafield_3) : '';
      const metafield1 = typeof raw1 === 'string' ? raw1.trim() : (raw1 || '');
      const metafield2 = typeof raw2 === 'string' ? raw2.trim() : (raw2 || '');
      const metafield3 = typeof raw3 === 'string' ? raw3.trim() : (raw3 || '');

      return {
        id: product.id,
        title: product.title,
        url: product.url,
        price: product.price,
        compare_at_price: product.compare_at_price,
        price_formatted: this.formatPrice(product.price),
        compare_at_price_formatted: product.compare_at_price ? this.formatPrice(product.compare_at_price) : null,
        available: product.available,
        vendor: product.vendor,
        sku: variant ? variant.sku : '',
        variant_id: variant ? variant.id : null,
        metafield_1: metafield1,
        metafield_2: metafield2,
        metafield_3: metafield3,
        featured_image: image ? { url: image.url, alt: image.alt || product.title } : null,
        secondary_image: product.secondary_image ? { url: product.secondary_image.url, alt: (product.secondary_image.alt || '') } : null
      };
    });
  }


  /**
   * Format price from JSON to currency string.
   * JSON stores price in hundredths of a cent (e.g. 14030000 = $1,403.00), so divide by 10000.
   */
  formatPrice(priceFromJson) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const amountInCents = Math.round(Number(priceFromJson) / 100);
      return Shopify.formatMoney(amountInCents, window.money_format || '${{amount}}');
    }
    const dollars = (Number(priceFromJson) / 10000).toFixed(2);
    return `$${parseFloat(dollars).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  /**
   * Initialize card scripts (for grouped products, etc.)
   * Runs after cards are in the DOM so script[type="application/json"] and variant
   * options exist; then calls init() so setInitialState/updateVariantAvailability run.
   */
  initializeCardScripts() {
    const groupedCards = this.container.querySelectorAll('grouped-product-card');
    if (groupedCards.length === 0) return;

    // Defer so DOM (and product JSON script) is fully parsed before init/setInitialState
    setTimeout(() => {
      groupedCards.forEach(card => {
        if (card.initialized) return;
        card.initialized = true;
        if (typeof card.init === 'function') {
          card.init();
        }
      });
    }, 50);
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionProductRenderer;
}
